/**
 * Deal page → "Texts" tab (Josh 10/12: "ADD A CONVERSATION FEATURE TO THE DEAL PAGES … AFTER DOCUMENTS").
 * The SMS conversation with the deal's contact (phone on file) + a composer. No collections case is
 * needed; if one is open the message is filed on it too. Replaces the read-only Summary card.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Phone, Send } from 'lucide-react';
import ContactPhoneFix from '../collections/ContactPhoneFix';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FundingCompanyBlob } from '../../pages/Collections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', promised: 'Promised', resolved: 'Resolved', closed: 'Closed' };
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const segmentsOf = (t) => { const n = (t || '').length; const gsm = [...(t || '')].every(c => c.charCodeAt(0) < 128); const a = gsm ? 160 : 70, b = gsm ? 153 : 67; return n <= a ? 1 : Math.ceil(n / b); };

export const renderTemplate = (body, vars) => (body || '').replace(/\{([a-z_]+)\}/g, (m, k) => (vars && vars[k] ? String(vars[k]) : m));

const DealTextsTab = ({ deal, getAuthHeaders, isAdmin }) => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const headers = useMemo(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);

  const load = useCallback(async () => {
    if (!deal?.id) return;
    try {
      const r = await axios.get(`${API}/collections/deals/${deal.id}/thread`, headers);
      setData(r.data);
      if (r.data.thread?.unread_count > 0) await axios.post(`${API}/collections/threads/${r.data.thread.id}/seen`, {}, headers);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load texts'); }
  }, [deal?.id, headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/collections/templates`, headers).then(r => setTemplates((r.data.templates || []).filter(t => t.active !== false))).catch(() => {});
  }, [headers]);
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [data?.messages]);

  if (!isAdmin) return <Card className="p-6 text-sm text-slate-500">Texting is available to admins.</Card>;
  if (!data) return <Card className="p-6 text-sm text-slate-500">Loading…</Card>;

  const ctx = data.context || {};
  const noPhone = !ctx.contact?.phone_e164;
  const optedOut = !!data.thread?.opted_out;
  const openCase = (data.cases || []).find(c => c.id === data.open_case_id);
  const suggested = data.suggested_template_id;

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    const t = templates.find(x => x.id === tid);
    if (t) setDraft(renderTemplate(t.body, { ...(ctx.variables || {}), promise_date: openCase?.promise_date || '' }));
  };

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/collections/deals/${deal.id}/messages`, { body: draft, template_id: templateId || null }, headers);
      toast.success(r.data.dry_run ? 'Stored as DRY RUN — Aircall is not configured' : 'Text sent');
      setDraft(''); setTemplateId('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
          <span key={`phone-${deal.id}-${ctx.contact?.phone_e164 || 'none'}`} className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />
            {noPhone ? <span className="text-red-600">no usable phone on the contact</span> : `${ctx.contact.phone_raw} (${ctx.contact.name || ctx.contact.company_name || 'contact'})`}
          </span>
          <span className="text-slate-500 inline-flex items-center gap-1.5"><FundingCompanyBlob entity={ctx.entity} /> Texts as {ctx.entity?.name}</span>
          {ctx.bounces?.last_date && <span className="text-slate-500">Last bounce {ctx.bounces.last_amount} on {ctx.bounces.last_date} {ctx.bounces.last_code && `(${ctx.bounces.last_code})`}</span>}
          {openCase && <Badge variant="outline">Case {STATUS_LABEL[openCase.status] || openCase.status}{openCase.promise_date ? ` · promised ${openCase.promise_date}` : ''}</Badge>}
          {!data.aircall_configured && <Badge variant="outline" className="border-amber-400 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" />dry run</Badge>}
        </div>
        <div className="flex gap-3 text-xs">
          {openCase && <Link to={`/collections?case=${openCase.id}`} className="text-blue-700 hover:underline">Open in Collections</Link>}
          {data.thread && <Link to={`/inbox?thread=${data.thread.id}`} className="text-blue-700 hover:underline">Open in Inbox</Link>}
        </div>
      </div>

      <div className="h-[46vh] overflow-y-auto p-4 space-y-2 bg-slate-50">
        {(data.messages || []).length === 0 && <div className="text-sm text-slate-500 text-center mt-10">No texts yet.</div>}
        {(data.messages || []).map(m => (
          <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.direction === 'out' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
              <div className="whitespace-pre-wrap">{m.body}</div>
              <div className={`text-[10px] mt-1 ${m.direction === 'out' ? 'text-blue-100' : 'text-slate-500'}`}>
                {fmtWhen(m.created_at)}{m.direction === 'out' ? ` · ${m.sent_by_email || 'sent'} · ${m.status}` : ' · merchant'}{m.error && <span className="text-red-200"> · {m.error}</span>}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="border-t p-3 space-y-2">
        {ctx.deal?.legal_stage && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="legal-stage-notice">
          This deal is in {ctx.deal?.status === 'Default' ? 'Default' : 'recovery'}{ctx.deal?.recovery_status ? ` (${ctx.deal.recovery_status})` : ''}. Automatic outreach never texts or emails it. Contacting the merchant directly may not be permitted (e.g. represented by counsel) — confirm before sending.
        </div>}
        {optedOut && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">This number replied STOP. Texting is blocked.</div>}
        {noPhone && <ContactPhoneFix dealId={deal.id} contactName={ctx.contact?.name || ctx.contact?.company_name} headers={headers} onSaved={load} />}
        <div className="flex gap-2 items-center">
          <Select value={templateId} onValueChange={applyTemplate}>
            <SelectTrigger className="w-72 h-9"><SelectValue placeholder="Template…" /></SelectTrigger>
            <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.id === suggested ? ' ★' : ''}</SelectItem>)}</SelectContent>
          </Select>
          {suggested && !draft && <Button variant="outline" className="h-9" onClick={() => applyTemplate(suggested)}>Use suggested</Button>}
          <span className="text-xs text-slate-500 ml-auto">{draft.length} chars · {segmentsOf(draft)} segment{segmentsOf(draft) === 1 ? '' : 's'}</span>
        </div>
        <Textarea rows={3} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Type a text…" disabled={optedOut || noPhone} />
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending || optedOut || noPhone || !draft.trim()}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}{data.aircall_configured ? 'Send' : 'Send (dry run)'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default DealTextsTab;
